import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardHeader from "../../components/app_components/DashboardHeader";
import DashboardSidebar from "../../components/app_components/DashboardSidebar";
import { 
  Home, Bell, BellDot, ShoppingBag, Settings, 
  Users, ShoppingCart, CreditCard as CreditCardIcon,
  RefreshCw, Zap, Shield, Share2, Gem, Lock 
} from 'lucide-react';
import LogoText from "../../images/logo_arrows.svg";

// Network logo URLs
const VISA_LOGO = "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/2560px-Visa_Inc._logo.svg.png";
const MASTERCARD_LOGO = "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/1280px-Mastercard-logo.svg.png";

const VirtualCard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [notifications] = useState({
    unreadCount: 1,
    hasUnread: true
  });
  const [cardNetwork, setCardNetwork] = useState('visa');
  const [isPremium] = useState(false);

  const [unreadCount, setUnreadCount] = useState(0);


  const fetchUnreadNotifications = async () => {
    try {
      const userId = localStorage.getItem("userID");
      if (!userId) return;

      const response = await fetch(
        `https://easysubs-1ad308994ada.herokuapp.com/api/notifications?user_id=${userId}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const notifications = await response.json();
      const unread = notifications.filter(notif => !notif.read_status).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };


  useEffect(() => {
    fetchUnreadNotifications();
    const interval = setInterval(() => {
      fetchUnreadNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, []);


  const navItems = [
    { icon: Home, label: "Home", path: "/dashboard" },
    { 
      icon: unreadCount > 0 ? BellDot : Bell,
      label: "Notifications",
      path: "/notifications",
      badge: unreadCount > 0 ? unreadCount : null
    },
    { icon: ShoppingCart, label: "Marketplace", path: "/marketplace" },
    { icon: Share2, label: "Share Subs", path: "/share-subs" },
    { icon: ShoppingBag, label: "Connect Bank", path: "/connectBank" },
    { icon: Users, label: "Partners", path: "/partners" },
    { icon: Settings, label: "Settings", path: "/account-settings" },
  ];

  const cardFeatures = [
    {
      icon: Shield,
      title: "Subscription Protection",
      description: "Dedicated card numbers for your online subscription"
    },
    {
      icon: Zap,
      title: "Instant Creation",
      description: "Generate new virtual cards when you subscribe"
    },
    {
      icon: RefreshCw,
      title: "Usage Control",
      description: "Set limits specific to each subscription"
    },
    {
      icon: CreditCardIcon,
      title: "Network Choice",
      description: "Select between Visa or Mastercard"
    }
  ];

  // Card data
  const [card] = useState({
    number: '•••• •••• •••• 4242',
    name: 'YOUR NAME',
    expiry: '••/••',
    cvv: '•••'
  });

  return (
    <div className="flex h-screen bg-gray-50">
      <DashboardSidebar
        isOpen={isSidebarOpen}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        navItems={navItems}
        currentPath={location.pathname}
        navigate={navigate}
      />

      <div className="flex-1 overflow-auto">
        <DashboardHeader isAuthenticated={!!localStorage.getItem("userID")} />
        
        <div className="p-6 space-y-6">
          <div className="bg-[#253985] text-white p-6 rounded-lg">
            <h1 className="text-2xl font-bold mb-2">Subscription Virtual Card</h1>
            <p className="text-white/90">Secure payment method for your online subscriptions</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Left Column */}
              <div className="lg:w-1/2">
                {/* Network Selection */}
                <div className="flex gap-4 mb-4">
                  <button 
                    onClick={() => setCardNetwork('visa')}
                    className={`flex-1 py-2 px-4 rounded-lg border-2 flex items-center justify-center gap-2 ${
                      cardNetwork === 'visa' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img src={VISA_LOGO} alt="Visa" className="h-6 object-contain" />
                  </button>
                  <button 
                    onClick={() => setCardNetwork('mastercard')}
                    className={`flex-1 py-2 px-4 rounded-lg border-2 flex items-center justify-center gap-2 ${
                      cardNetwork === 'mastercard' 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img src={MASTERCARD_LOGO} alt="Mastercard" className="h-8 object-contain" />
                    
                  </button>
                </div>

                {/* Credit Card Design */}
                <div className="border-2 border-[#253985] bg-white rounded-2xl p-6 h-64 shadow-md relative overflow-hidden">
                  {/* Your Logo - Top Right (Larger) */}
                  <img 
                    src={LogoText} 
                    alt="Company Logo" 
                    className="absolute top-8 right-6 h-20" // Increased size to h-16
                    onError={(e) => e.target.style.display = 'none'}
                  />

                  {/* Card Number */}
                  <div className="text-xl tracking-wider font-medium mt-10 mb-6 text-gray-800">
                    {card.number}
                  </div>
                  
                  {/* Network Logo - Bottom Right */}
                  <div className="absolute bottom-20 right-8">
                    <img 
                      src={cardNetwork === 'visa' ? VISA_LOGO : MASTERCARD_LOGO} 
                      alt={cardNetwork} 
                      className="h-10 object-contain"
                    />
                  </div>
                  
                  {/* Card Details */}
                  <div className="flex justify-between items-end absolute bottom-6 left-6 right-12 text-gray-800">
                    <div>
                      <div className="text-xs opacity-60">CARD HOLDER</div>
                      <div className="text-sm font-medium">{card.name}</div>
                    </div>
                    <div>
                      <div className="text-xs opacity-60">EXPIRES</div>
                      <div className="text-sm font-medium">{card.expiry}</div>
                    </div>
                    <div>
                      <div className="text-xs opacity-60">CVV</div>
                      <div className="text-sm font-medium">{card.cvv}</div>
                    </div>
                  </div>
                </div>
                
                {/* Premium Upgrade */}
                <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg flex items-center">
                  <Gem className="h-6 w-6 text-blue-600 mr-3" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800">Premium Feature</h3>
                    <p className="text-sm text-gray-600">Upgrade to generate virtual cards for your subscriptions</p>
                  </div>
                  <button 
                    onClick={() => navigate('/upgrade')}
                    className="px-4 py-2 bg-[#253985] text-white rounded-lg hover:bg-[#1a2c6d] text-sm"
                  >
                    Upgrade Now
                  </button>
                </div>
                
                {/* Action Buttons */}
                <div className="mt-4 flex gap-4">
                  <button 
                    disabled={!isPremium}
                    className={`px-4 py-2 rounded-lg flex-1 flex items-center justify-center gap-2 ${
                      isPremium 
                        ? 'bg-[#253985] text-white hover:bg-[#1a2c6d]'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {!isPremium && <Lock className="h-4 w-4" />}
                    Generate Subscription Card
                  </button>
                  <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex-1">
                    Copy Card Details
                  </button>
                </div>
              </div>
              
              {/* Right Column */}
              <div className="lg:w-1/2">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">How Subscription Cards Work</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {cardFeatures.map((feature, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg hover:shadow-md">
                      <feature.icon className="h-6 w-6 text-[#253985] mb-2" />
                      <h3 className="text-lg font-semibold mb-1">{feature.title}</h3>
                      <p className="text-gray-600 text-sm">{feature.description}</p>
                    </div>
                  ))}
                </div>
                
                <div className="mt-8">
                  <h3 className="text-lg font-semibold mb-4">Your Subscription Cards</h3>
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-medium">Netflix</div>
                          <div className="text-sm text-gray-600">•••• 4242 (Visa)</div>
                        </div>
                        <div className="text-sm text-gray-600">$15.99/month</div>
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-medium">Spotify</div>
                          <div className="text-sm text-gray-600">•••• 1312 (Mastercard)</div>
                        </div>
                        <div className="text-sm text-gray-600">$9.99/month</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex flex-col md:flex-row items-center justify-between">
                <div className="mb-4 md:mb-0">
                  <h3 className="text-lg font-semibold text-gray-800">Need help with subscription cards?</h3>
                  <p className="text-gray-600">Learn how to use virtual cards for safer subscriptions</p>
                </div>
                <button 
                  onClick={() => navigate('/support')}
                  className="px-6 py-3 bg-[#253985] text-white rounded-lg hover:bg-[#1a2c6d]"
                >
                  View Guide
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VirtualCard;